import { z } from 'zod';
import { RoomCodeSchema } from './ids.js';
/**
 * Zod schemas for EVERY client -> server socket message.
 *
 * SECURITY INVARIANT: no message here carries a player identity. The sender is
 * always taken from the authenticated socket session (`socket.data.user`), never
 * from the payload. A client therefore cannot submit an event as its opponent,
 * even though it knows the opponent exists and knows the matchId.
 *
 * The orchestrator never inspects `event` — it is `unknown` by construction and
 * is handed opaquely to the engine registered for `minigameId`. See plan doc
 * §8A.2: the instant the orchestrator inspects a payload, plug-and-play is dead.
 */
/** Loose matchId shape. Prisma cuid()s; we only bound the length here. */
const MatchIdSchema = z.string().min(1).max(64);
export const QueueJoinMessage = z.object({});
export const QueueLeaveMessage = z.object({});
export const RoomCreateMessage = z.object({});
export const RoomJoinMessage = z.object({
    code: RoomCodeSchema,
});
export const RoomCancelMessage = z.object({});
export const MatchReadyMessage = z.object({
    matchId: MatchIdSchema,
});
export const MatchLeaveMessage = z.object({
    matchId: MatchIdSchema,
});
/**
 * The one hot-path message.
 *
 * `event` is `z.unknown()` on purpose. The gateway validates only the envelope
 * — that this is a real match, that the sender is in it, and that the segment
 * index is current. The *payload* is parsed by the engine's own schema inside
 * `applyEvent`. This is what lets a future canvas-drawing game share this exact
 * envelope with zero gateway changes.
 */
export const MinigameEventMessage = z.object({
    matchId: MatchIdSchema,
    /**
     * Which segment of the recipe this event targets. Guards against a lagging
     * client's events being applied to the wrong (already advanced) segment.
     */
    segmentIndex: z.number().int().min(0).max(63),
    event: z.unknown(),
});
/**
 * The fixed set of in-game reactions (emotes). A closed set — not free text —
 * so there is nothing to moderate, the payload is one small enum, and a client
 * can never inject arbitrary content. Order is the palette order in the UI.
 */
export const REACTIONS = ['🫪', '😂', '🔥', '😮', '😭', '🧠', '🎯', '🐢'];
/** Fire an emote in the current match. Relayed to both players. */
export const ReactionMessage = z.object({
    matchId: MatchIdSchema,
    reaction: z.enum(REACTIONS),
});
/**
 * Offer (or accept) a rematch of the match that just finished. Both players
 * offering the same finished match starts a new one between exactly the two of
 * them. Sending it again while waiting is a no-op.
 */
export const RematchOfferMessage = z.object({
    matchId: MatchIdSchema,
});
/** Withdraw a pending rematch offer (e.g. leaving the result screen). */
export const RematchCancelMessage = z.object({
    matchId: MatchIdSchema,
});
/** Names of client -> server socket events. */
export const CLIENT_EVENTS = {
    queueJoin: 'queue:join',
    queueLeave: 'queue:leave',
    roomCreate: 'room:create',
    roomJoin: 'room:join',
    roomCancel: 'room:cancel',
    matchReady: 'match:ready',
    matchLeave: 'match:leave',
    minigameEvent: 'minigame:event',
    reaction: 'reaction:send',
    rematchOffer: 'rematch:offer',
    rematchCancel: 'rematch:cancel',
};
/**
 * Maps each inbound event name to its schema. The gateway iterates this table
 * to register handlers, so an unvalidated inbound event is structurally
 * impossible — you cannot add a listener without adding a schema.
 */
export const CLIENT_EVENT_SCHEMAS = {
    [CLIENT_EVENTS.queueJoin]: QueueJoinMessage,
    [CLIENT_EVENTS.queueLeave]: QueueLeaveMessage,
    [CLIENT_EVENTS.roomCreate]: RoomCreateMessage,
    [CLIENT_EVENTS.roomJoin]: RoomJoinMessage,
    [CLIENT_EVENTS.roomCancel]: RoomCancelMessage,
    [CLIENT_EVENTS.matchReady]: MatchReadyMessage,
    [CLIENT_EVENTS.matchLeave]: MatchLeaveMessage,
    [CLIENT_EVENTS.minigameEvent]: MinigameEventMessage,
    [CLIENT_EVENTS.reaction]: ReactionMessage,
    [CLIENT_EVENTS.rematchOffer]: RematchOfferMessage,
    [CLIENT_EVENTS.rematchCancel]: RematchCancelMessage,
};
//# sourceMappingURL=socket-events.js.map